import { AuthService as AuthServiceAPI } from '@/services/Auth';

// Extended User interface that matches UserType structure
export interface User {
  id: number;
  email: string;
  name?: string;
  org: string;
  userTag: string;
  taxId?: string;
  pixKey?: string;
  phoneNumber?: string;
  apiKey?: string;
  color?: string;
  isCorporate?: boolean;
  hasApi?: boolean;
  createdAt?: string;
  updatedAt?: string;
  userType?:
    | 'CorporateGold'
    | 'CorporateSilver'
    | 'CorporateBronze'
    | 'Regular'
    | 'PersonalGold'
    | 'Dashboard';
}

export interface AuthResponse {
  user: User;
  access_token: string;
}

// Helper function to map API user data to complete User structure
function mapApiUserToUser(apiUser: any, email: string): User {
  return {
    id: parseInt(apiUser.id) || Math.floor(Math.random() * 10000),
    email: email,
    name: apiUser.name || email.split('@')[0],
    org: apiUser.org || 'pagar',
    userTag:
      apiUser.userTag ||
      email
        .split('@')[0]
        .toLowerCase()
        .replace(/[^a-z0-9]/g, ''),
    taxId: apiUser.taxId,
    pixKey: apiUser.pixKey,
    phoneNumber: apiUser.phoneNumber,
    apiKey: apiUser.client_id || apiUser.apiKey,
    color: apiUser.color || '#000080',
    isCorporate: apiUser.isCorporate || false,
    hasApi: !!(apiUser.client_id || apiUser.apiKey),
    createdAt: apiUser.createdAt || new Date().toISOString(),
    updatedAt: apiUser.updatedAt || new Date().toISOString(),
    userType: apiUser.userType || 'PersonalGold',
  };
}

class AuthService {
  async login(email: string): Promise<{ message: string; success: boolean }> {
    return AuthServiceAPI.login(email);
  }

  async validateCode(
    email: string,
    code: string
  ): Promise<{ user: User; access_token: string }> {
    try {
      const response = await AuthServiceAPI.validation(email, code);

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Validation failed');
      }

      console.log('AuthService.validateCode response:', response);

      let userData = response.data.user;

      // Se não tem dados de usuário na resposta, extrair do JWT token
      if (!userData && response.data.access_token) {
        try {
          const tokenPayload = JSON.parse(
            atob(response.data.access_token.split('.')[1])
          );
          console.log('Extracted from JWT:', tokenPayload);
          userData = {
            id: tokenPayload.id,
            name: tokenPayload.name,
            email: tokenPayload.email,
            org: tokenPayload.org,
            userTag: tokenPayload.userTag,
            roles: tokenPayload.roles,
            phoneNumber: tokenPayload.phoneNumber,
            userType: 'PersonalGold', // Default
          };
        } catch (jwtError) {
          console.error('Error parsing JWT:', jwtError);
          userData = {}; // Fallback to empty object
        }
      }

      // Map the API user data to our complete User structure
      const completeUser = mapApiUserToUser(userData, email);

      return {
        user: completeUser,
        access_token: response.data.access_token,
      };
    } catch (error) {
      console.error('AuthService.validateCode error:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    // Clear stored user data
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
  }

  getCurrentUser(): User | null {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return null;

      const user = JSON.parse(userData);
      // Ensure the user data has all required fields
      if (user && user.email) {
        return user as User;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!token && !!user;
  }

  storeAuth(user: User, token: string): void {
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('access_token', token);
  }
  
  updateUser(user: User): void {
    localStorage.setItem('user', JSON.stringify(user));
  }
}

export const authService = new AuthService();
